import { getDomainLanguage } from "@/hooks/getDomain";
import replaceAllBracketedStrings, { replaceSingleBracketWithString } from "../hooks/replaceKeyWithNumber";
import { extractPercentageStrings } from "../hooks/useRegex";
import { useEffect, useState } from "react";
import Checkbox from "./Checkbox";
import FilterBtn from "./FilterBtn";
import Verified from "./Verified";

// Config array for savings type pills — avoids 20 repetitive button blocks
const SAVINGS_CONFIG = [
  ['sale', 'savings_sale'],
  ['bogo', 'savings_bogo'],
  ['bundle', 'savings_bundle'],
  ['welcome_bonus', 'savings_welcome'],
  ['newsletter', 'savings_newsletter'],
  ['free_shipping', 'savings_freeshipping'],
  ['loyalty_programme', 'savings_loyalty'],
  ['student_discount', 'savings_student'],
  ['military_discount', 'savings_military'],
  ['first_order_discount', 'savings_firstorder'],
  ['clearance', 'savings_clearance'],
  ['flash_sales', 'savings_flash'],
  ['app_discount', 'savings_app'],
  ['payment_discount', 'savings_payment'],
  ['cashback', 'savings_cashback'],
  ['seasonal', 'savings_seasonal'],
  ['free_trial', 'savings_freetrial'],
  ['deals', 'savings_deals'],
  ['gift_card', 'savings_giftcard'],
  ['wholesale', 'savings_wholesale'],
  ['subscription', 'savings_subscription'],
  ['price_match', 'savings_pricematch'],
  ['senior', 'savings_senior'],
  ['teacher', 'savings_teacher'],
  ['healthcare', 'savings_healthcare'],
  ['employee', 'savings_employee'],
];

// Map savings signal keys to offer_type values for filtering
const SAVINGS_OFFER_TYPE_MAP = {
  sale: ['sale'],
  bogo: ['bogo', 'multi_buy'],
  bundle: ['bundle', 'multi_buy'],
  welcome_bonus: ['referral', 'free_gift', 'birthday_rewards'],
  newsletter: ['newsletter', 'newsletter_discount'],
  free_shipping: ['free_shipping'],
  loyalty_programme: ['loyalty', 'membership_benefits', 'membership', 'loyalty_programme'],
  student_discount: ['student'],
  military_discount: ['military', 'military_discount', 'first_responder'],
  first_order_discount: ['first_order'],
  clearance: ['clearance', 'outlet', 'open_box', 'refurbished'],
  flash_sales: ['flash_sale'],
  app_discount: ['app_discount'],
  payment_discount: ['payment_discount', 'financing', 'buy_now_pay_later'],
  cashback: ['cashback'],
  seasonal: ['seasonal', 'seasonal_sale'],
  free_trial: ['free_trial'],
  deals: ['deals', 'daily_deal', 'daily_deals', 'deal', 'promotion', 'discount'],
  gift_card: ['gift_card', 'gift', 'gift_cards', 'gift_with_purchase'],
  free_shipping: ['free_shipping', 'free_returns', 'free_return', 'shipping_discount'],
  newsletter: ['newsletter', 'newsletter_discount'],
  bogo: ['bogo', 'multi_buy'],
  bundle: ['bundle'],
  welcome_bonus: ['referral', 'free_gift', 'birthday_rewards'],
  wholesale: ['wholesale', 'bulk_discount', 'bulk', 'bulk_order', 'volume_discount'],
  subscription: ['subscription', 'subscribe_save', 'subscribe_and_save'],
  price_match: ['price_match'],
  senior: ['senior', 'senior_discount'],
  teacher: ['teacher', 'teacher_discount'],
  healthcare: ['healthcare'],
  employee: ['employee_discount'],
};

export default function Filter({
  filteredOffers,
  allOffers,
  store,
  error,
  query,
  setFilteredOffers,
  topStores,
  translation,
  currentEvent,
  domain
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [totalHits, setTotalHits] = useState(0);
  const [activeSavings, setActiveSavings] = useState([]);
  
  const averageViews = Math.max(1,Math.round(totalHits / Math.max(12, Math.sqrt(Math.max(1, totalHits)))));
  
  const filter = () => {
    const checked = [];
    const checkboxes = document.querySelectorAll("input[type=checkbox]");

    if (checkboxes) {
      checkboxes.forEach((checkbox) => {
        if (checkbox.checked) {
          checked.push(checkbox.name);
        }
      });
    }
    
    // Apply discount type filter first
    let typeFiltered = allOffers.filter((coupon) => {
      if (checked.includes("codes") && coupon.code !== "") {
        return true;
      }
      if (checked.includes("offers") && coupon.code === "") {
        return true;
      }
      if (checked.includes("discount") && coupon.title.includes("%")) {
        return true;
      }
      if ((checked.includes("discount2") && coupon.title.includes("$")) || coupon.title.includes("€")) {
        return true;
      } else {
        return false;
      }
    });
    if (checked.length === 0) {
      typeFiltered = allOffers;
    }

    // Apply savings type filter on top
    let finalFiltered = typeFiltered;
    if (activeSavings.length > 0) {
      const allowedTypes = new Set();
      activeSavings.forEach(key => {
        (SAVINGS_OFFER_TYPE_MAP[key] || []).forEach(t => allowedTypes.add(t));
      });
      finalFiltered = typeFiltered.filter(coupon => {
        const ot = coupon.offer_type || '';
        return allowedTypes.has(ot);
      });
    }

    setFilteredOffers(finalFiltered);
  };

  const lengthOfFilteringOffers = () => {
    return [
      allOffers.filter((coupon) => coupon.code !== "").length,
      allOffers.filter((coupon) => coupon.code === "").length,
      allOffers.filter((coupon) => coupon.title.includes("%")).length,
      allOffers.filter(
        (coupon) => coupon.title.includes("$") || coupon.title.includes("€")
      ).length,
    ];
  };

  const mobileFilter = (index) => {
    if (index === 0) {
      setFilteredOffers(allOffers);
    }
    if (index === 1) {
      setFilteredOffers(allOffers.filter((offer) => offer.code !== ""));
    }
    if (index === 2) {
      setFilteredOffers(allOffers.filter((offer) => offer.code === ""));
    }
    if (index === 3) {
      setFilteredOffers(allOffers.filter((offer) => offer.title.includes("%")));
    }
    if (index === 4) {
      setFilteredOffers(
        allOffers.filter(
          (offer) => offer.title.includes("$") || offer.title.includes("€") || offer.title.includes("£")
        )
      );
    }
  };

  // Compute discount stats from allOffers
  const discountStats = (() => {
    const pcts = allOffers
      .map(o => {
        const m = (o.title || '').match(/(\d+)%/);
        return m ? parseInt(m[1]) : null;
      })
      .filter(x => x !== null);
    if (pcts.length === 0) return null;
    return {
      avg: Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length),
      max: Math.max(...pcts),
      count: pcts.length,
    };
  })();

  // Savings signals — derived from offer_type and pickedAs fields on offers
  // Also tracks offer counts per signal for display
  const savingsSignals = (() => {
    if (!allOffers || allOffers.length === 0) return null;
    const signals = {};
    SAVINGS_CONFIG.forEach(([key]) => { signals[key] = { active: false, count: 0 }; });
    
    for (const o of allOffers) {
      const ot = o.offer_type || '';
      const pa = o.pickedAs || '';
      if (ot === 'sale') { signals.sale.active = true; signals.sale.count++; }
      if (ot === 'bundle' || ot === 'multi_buy') { signals.bundle.active = true; signals.bundle.count++; }
      if (ot === 'multi_buy' || ot === 'bogo') { signals.bogo.active = true; signals.bogo.count++; }
      if (ot === 'newsletter' || ot === 'newsletter_discount') { signals.newsletter.active = true; signals.newsletter.count++; }
      if (ot === 'free_shipping') { signals.free_shipping.active = true; signals.free_shipping.count++; }
      if (ot === 'loyalty' || ot === 'membership_benefits') { signals.loyalty_programme.active = true; signals.loyalty_programme.count++; }
      if (ot === 'student') { signals.student_discount.active = true; signals.student_discount.count++; }
      if (ot === 'military') { signals.military_discount.active = true; signals.military_discount.count++; }
      if (ot === 'first_order') { signals.first_order_discount.active = true; signals.first_order_discount.count++; }
      if (ot === 'clearance' || ot === 'outlet') { signals.clearance.active = true; signals.clearance.count++; }
      if (ot === 'flash_sale') { signals.flash_sales.active = true; signals.flash_sales.count++; }
      if (ot === 'referral' || ot === 'free_gift' || ot === 'birthday_rewards') { signals.welcome_bonus.active = true; signals.welcome_bonus.count++; }
      if (ot === 'app_discount') { signals.app_discount.active = true; signals.app_discount.count++; }
      if (ot === 'payment_discount') { signals.payment_discount.active = true; signals.payment_discount.count++; }
      if (ot === 'cashback') { signals.cashback.active = true; signals.cashback.count++; }
      if (ot === 'seasonal') { signals.seasonal.active = true; signals.seasonal.count++; }
      if (ot === 'free_trial') { signals.free_trial.active = true; signals.free_trial.count++; }
      if (ot === 'deals') { signals.deals.active = true; signals.deals.count++; }
      if (ot === 'gift_card') { signals.gift_card.active = true; signals.gift_card.count++; }
      if (pa === 'recurring_offer') {
        if (!ot || ot === 'sale') { signals.sale.active = true; if (ot !== 'sale') signals.sale.count++; }
      }
    }
    return signals;
  })();
  const hasSavingsSignals = savingsSignals && Object.values(savingsSignals).some(v => v.active);

  // Helper to validate officialdata
  const hasValidOfficialData = (() => {
    const html = store?.officialdata?.trim();
    if (!html) return false;
    const liCount = (html.match(/<li\b/gi) || []).length;
    return liCount > 0 && liCount <= 6;
  })();

  // Function to format date
  const getFormattedDate = (locale) => {
    const date = new Date();
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
  };
  const monthyear = getFormattedDate(translation?.locale);

  const toggleSavings = (key) => {
    setActiveSavings(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  useEffect(() => {
    const inputs = document.querySelectorAll("input[type=checkbox]");
    Array.from(inputs).map((inp) => {
      inp.checked = false;
    });
    setActiveSavings([]);
  }, [query]);

  // Re-run filter when savings selection changes
  useEffect(() => {
    filter();
  }, [activeSavings]);

  useEffect(() => {
    const allHits = filteredOffers.map((e) => parseInt(e.hits));

    setTotalHits(allHits.reduce((partialSum, a) => partialSum + a, 0));
  }, [filteredOffers]);

  const tabs = [
    translation?.filter_all,
    `${replaceSingleBracketWithString(
      translation?.filter_codes,
      lengthOfFilteringOffers()[0]
    )}`,
    `${replaceSingleBracketWithString(
      translation?.filter_offers,
      lengthOfFilteringOffers()[1]
    )}`,
    `${replaceSingleBracketWithString(
      translation?.filter_percent,
      lengthOfFilteringOffers()[2]
    )}`,
    `${replaceSingleBracketWithString(
      translation?.filter_discount,
      lengthOfFilteringOffers()[3]
    )})`,
  ];

  return (
    translation && (
      <div className="lg:w-56">
        <div className="flex items-center md:gap-8 gap-4">
          {!error && (
            <div className="hidden lg:block w-full bg-white p-6">
              <img src={store?.logoUrl} alt={`${store?.name} Logo`} width="300" height="240" loading="lazy" />
            </div>
          )}
        </div>
        <div className="lg:hidden mt-4 md:mt-2 overflow-x-auto pb-4 w-full">
          <div className="flex w-max items-center gap-2.5 md:gap-5">
            {tabs.map((btnText, index) => {
              return (
                <FilterBtn
                  key={index}
                  onClick={() => mobileFilter(index)}
                  text={btnText}
                  setActiveTab={setActiveTab}
                  active={activeTab}
                  index={index}
                />
              );
            })}
          </div>
        </div>
        {filteredOffers.length === 0 && (
          <p className="md:hidden text-red-500">No Coupon Found</p>
        )}
        <div className="w-full lg:block hidden">
          <div className="mt-4 flex flex-col gap-3">
              <div className="flex items-center overflow-hidden bg-white">
                <span className="block shrink-0 bg-gray-450 px-4 py-3 text-primary font-semibold w-14 text-center">
                  {filteredOffers.length}
                </span>
                <span className="px-3 text-sm text-gray-light">
                  {translation?.offersavailable}
                </span>
              </div>
              {lengthOfFilteringOffers()[0] > 0 && (
                <div className="flex items-center overflow-hidden bg-white">
                  <span className="block shrink-0 bg-gray-450 px-4 py-3 text-primary font-semibold w-14 text-center">✓</span>
                  <span className="px-3 text-sm text-gray-light">
                    {replaceSingleBracketWithString(
                      translation?.offerverified,
                      lengthOfFilteringOffers()[0]
                    )}
                  </span>
                </div>
              )}
              {totalHits > 0 && (
                <div className="flex items-center overflow-hidden bg-white">
                  <span className="block shrink-0 bg-gray-450 px-4 py-3 text-primary font-semibold w-14 text-center">✓</span>
                  <span className="px-3 text-sm text-gray-light">
                    {replaceSingleBracketWithString(
                      translation?.offerusedtoday,
                      averageViews
                    )}
                  </span>
                </div>
              )}
              {discountStats && (
                <div className="flex items-center overflow-hidden bg-white">
                  <span className="block shrink-0 bg-gray-450 px-4 py-3 text-primary font-semibold w-14 text-center">
                    {discountStats.max}%
                  </span>
                  <span className="px-3 text-sm text-gray-light">
                    {translation?.max_discount_label || 'Best discount available'}
                  </span>
                </div>
              )}
              {discountStats && discountStats.count > 1 && (
                <div className="flex items-center overflow-hidden bg-white">
                  <span className="block shrink-0 bg-gray-450 px-4 py-3 text-primary font-semibold w-14 text-center">
                    {discountStats.avg}%
                  </span>
                  <span className="px-3 text-sm text-gray-light">
                    {translation?.avg_discount_label || 'Average discount this month'}
                  </span>
                </div>
              )}
            </div>
          <hr className="border-dashed my-5 border-gray-300" />
          <form>
            <p>{translation?.filter_title}</p>
            <div className="mt-4 flex flex-col gap-3">
              <Checkbox
                onClick={(e) => {
                  filter(e.target.checked, "code");
                }}
                name="codes"
                text={`${replaceSingleBracketWithString(
                  translation?.filter_codes,
                  lengthOfFilteringOffers()[0]
                )}`}
              />
              <Checkbox
                onClick={(e) => {
                  filter(e.target.checked, "withoutCode");
                }}
                name="offers"
                text={`${replaceSingleBracketWithString(
                  translation?.filter_offers,
                  lengthOfFilteringOffers()[1]
                )}`}
              />
              <Checkbox
                onClick={(e) => {
                  filter(e.target.checked, "discount");
                }}
                name="discount"
                text={`${replaceSingleBracketWithString(
                  translation?.filter_percent,
                  lengthOfFilteringOffers()[2]
                )}`}
              />
              <Checkbox
                onClick={(e) => {
                  filter(e.target.checked, "discount2");
                }}
                name="discount2"
                text={`${replaceSingleBracketWithString(
                  translation?.filter_discount,
                  lengthOfFilteringOffers()[3]
                )}`}
              />
            </div>
          </form>
          {hasValidOfficialData && (
            <>
              <hr className="border-dashed my-5 border-gray-300" />
              <p>{translation?.officialdata_title}</p>
              <ul
                className="offdata mt-1.5 text-sm text-gray-light"
                dangerouslySetInnerHTML={{ __html: store.officialdata }}
              />
            </>
          )}
          {hasSavingsSignals && (
            <>
              <hr className="border-dashed my-5 border-gray-300" />
              <p>{translation?.savings_types}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {SAVINGS_CONFIG
                  .filter(([key, labelKey]) => savingsSignals[key]?.active && translation?.[labelKey])
                  .map(([key, labelKey]) => (
                    <button
                      key={key}
                      onClick={() => toggleSavings(key)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        activeSavings.includes(key)
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-light border-gray-200 hover:border-primary'
                      }`}
                    >
                      {translation?.[labelKey]}
                      <span className="ml-1 opacity-60">({savingsSignals[key].count})</span>
                    </button>
                  ))}
              </div>
            </>
          )}
            {true && (
              <>
                <hr className="border-dashed my-5 border-gray-300" />
                <p>{translation?.popularstores}</p>
            
                <div className="mt-4 grid grid-cols-2 gap-2.5">
                  {topStores.map((store, index) => {
                    return (
                      <a
                        href={`https://${store?.merchantSubdomain}.${domain}/`}
                        key={index}
                        className="bg-white p-2.5 h-10 flex justify-center"
                      >
                        <img
                          src={store.merchantLogo}
                          alt={store?.merchantName}
                          width="70"
                          height="50"
                          className="h-full object-contain"
                          loading="lazy"
                        />
                      </a>
                    );
                  })}
                </div>
              </>
            )}
            {currentEvent && currentEvent.length > 0 && currentEvent.map((ev) => (
              <div key={ev.slug}>
                <hr className="border-dashed my-5 border-gray-300" />
                <a
                  href={`https://www.${domain}/${ev.slug}`}
                  className="block bg-white p-4 rounded hover:shadow-md transition-shadow"
                >
                  <span className="block text-sm font-semibold text-primary text-center">
                    {ev.name}
                  </span>
                </a>
              </div>
            ))}
        </div>
      </div>
    )
  );
}